import React, { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { CheckCircle2, Eye, Pencil, Plus, RotateCcw, Save, Search, Trash2, XCircle } from 'lucide-react';
import SearchableProductInput from '../../components/common/SearchableProductInput';
import FilterSelect from '../../components/common/FilterSelect';
import Modal from '../../components/common/Modal';
import { dairyOrdersAPI, productsAPI } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { useStoreContextStore } from '../../store/storeContextStore';

const todayKey = () => new Date().toISOString().split('T')[0];
const shiftDate = (dateKey, days) => {
  const date = new Date(`${dateKey}T00:00:00`);
  if (Number.isNaN(date.getTime())) return todayKey();
  date.setDate(date.getDate() + days);
  return date.toISOString().split('T')[0];
};
const defaultSavedEntriesFromDate = () => shiftDate(todayKey(), -6);

const SAVED_ENTRY_STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'saved', label: 'Saved' },
  { value: 'confirmed', label: 'Confirmed' },
];
const memoPattern = /^[A-Za-z0-9]+(?:[-_/][A-Za-z0-9]+)*$/;
const createRowId = () => {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
};
const createEmptyRow = () => ({
  id: createRowId(),
  productKey: '',
  productInput: '',
  productId: '',
  productName: '',
  sku: '',
  quantity: '',
});
const sanitizeOrderRows = (rows = []) => rows
  .filter((row) => row.productInput || row.quantity)
  .map((row) => ({
    id: row.id,
    productKey: normalizeText(row.productKey),
    productInput: normalizeText(row.productInput),
    productId: normalizeText(row.productId),
    productName: normalizeText(row.productName),
    sku: normalizeText(row.sku),
    quantity: toPositiveInteger(row.quantity),
  }));
const mapLineItemToRow = (item = {}) => ({
  id: createRowId(),
  productKey: normalizeText(item.productKey || item.product_key || item.productId || item.product_id || item.sku),
  productInput: normalizeText(item.productName),
  productId: normalizeText(item.productId),
  productName: normalizeText(item.productName),
  sku: normalizeText(item.sku),
  quantity: String(toPositiveInteger(item.quantity) || ''),
});

const normalizeText = (value) => String(value ?? '').trim();
const toPositiveInteger = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? Math.round(numeric) : 0;
};
const formatDate = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
const formatDateTime = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleString();
};
const TRANSACTION_TYPE_OPTIONS = [
  { value: 'inward', label: 'Inward' },
  { value: 'outward', label: 'Outward' },
];
const DAIRY_RECORD_SOURCE = 'darkstore_dairy_order_records';
const getProductDuplicateKey = (value) => normalizeText(value).toLowerCase();

const DailyDairyOrderRecording = () => {
  const navigate = useNavigate();
  const { user, hasFeatureAccess } = useAuthStore();
  const selectedStore = useStoreContextStore((state) => state.selectedStore);
  const canViewPage = hasFeatureAccess('dairy_order.task');
  const selectedStoreContext = useMemo(() => ({
    storeInfoId: selectedStore?.storeInfoId || selectedStore?.store_info_id || null,
    vendorId: selectedStore?.vendorId || selectedStore?.vendor_id || null,
    storeName: selectedStore?.name || selectedStore?.storeName || selectedStore?.store_code || selectedStore?.storeCode || '',
  }), [selectedStore]);
  const hasStoreContext = Boolean(selectedStoreContext.storeInfoId && selectedStoreContext.vendorId);
  const [transactionType, setTransactionType] = useState('inward');
  const [transactionDate, setTransactionDate] = useState(todayKey());
  const [receivedBy, setReceivedBy] = useState(user?.name || 'Current User');
  const [adjustedBy, setAdjustedBy] = useState('');
  const [memoNumber, setMemoNumber] = useState('');
  const [rows, setRows] = useState([createEmptyRow()]);
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [errors, setErrors] = useState({});
  const [previewData, setPreviewData] = useState(null);
  const [draftMeta, setDraftMeta] = useState({ draftId: '', sourceRecordId: '', sourceKind: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [savedEntries, setSavedEntries] = useState([]);
  const [loadingSavedEntries, setLoadingSavedEntries] = useState(false);
  const [savedSearch, setSavedSearch] = useState('');
  const deferredSavedSearch = useDeferredValue(savedSearch);
  const [savedStatusFilter, setSavedStatusFilter] = useState('all');
  const [savedFromDate, setSavedFromDate] = useState(defaultSavedEntriesFromDate);
  const [savedToDate, setSavedToDate] = useState(todayKey);
  const [refreshKey, setRefreshKey] = useState(0);
  const [lastSavedEntryId, setLastSavedEntryId] = useState('');
  const [editingRecordId, setEditingRecordId] = useState('');
  const [editingRecordSource, setEditingRecordSource] = useState('');
  const [editingBaselineEntry, setEditingBaselineEntry] = useState(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [confirmedEntryMeta, setConfirmedEntryMeta] = useState(null);

  useEffect(() => {
    if (transactionType === 'inward') {
      setReceivedBy(user?.name || 'Current User');
    }
  }, [transactionType, user?.name]);

  useEffect(() => {
    if (!canViewPage) return;
    if (!hasStoreContext) {
      setProducts([]);
      return;
    }

    const loadProducts = async () => {
      setLoadingProducts(true);
      try {
        const response = await productsAPI.getClassified({
          classificationCode: 'dairy',
          storeInfoId: selectedStoreContext.storeInfoId,
          vendorId: selectedStoreContext.vendorId,
          limit: 2000,
        });
        const nextProducts = (response.data?.data || [])
          .map((product) => {
            const resolvedId = normalizeText(
              product.storeProductId
              || product.store_product_id
              || product.productId
              || product.product_id
              || product.id
              || product.sku
              || product.SKU
            );
            const vendorId = normalizeText(product.vendorId || product.vendor_id || selectedStoreContext.vendorId);
            const sellerId = normalizeText(product.sellerId || product.seller_id || '0');
            const name = normalizeText(
              product.name
              || product.product_name
              || product.product
              || product.Name
            );
            const sku = normalizeText(product.sku || product.SKU);
            const productKey = [vendorId, sellerId || '0', sku || resolvedId]
              .filter(Boolean)
              .join('::')
              .toLowerCase();
            return {
              id: resolvedId,
              key: productKey || resolvedId,
              productKey: productKey || resolvedId,
              name,
              sku,
              label: sku ? `${name} (${sku})` : name,
            };
          })
          .filter((product) => product.id && product.name)
          .sort((left, right) => left.label.localeCompare(right.label));
        setProducts(nextProducts);
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Failed to load dairy products');
      } finally {
        setLoadingProducts(false);
      }
    };

    loadProducts();
  }, [canViewPage, hasStoreContext, selectedStoreContext.storeInfoId, selectedStoreContext.vendorId]);

  useEffect(() => {
    if (!canViewPage) return;
    if (!hasStoreContext) {
      setSavedEntries([]);
      return;
    }

    let isMounted = true;

    const loadSavedEntries = async () => {
      setLoadingSavedEntries(true);
      try {
        const response = await dairyOrdersAPI.getMine({
          limit: 100,
          fromDate: savedFromDate,
          toDate: savedToDate,
          search: normalizeText(deferredSavedSearch) || undefined,
          status: savedStatusFilter,
          storeInfoId: selectedStoreContext.storeInfoId,
          vendorId: selectedStoreContext.vendorId,
        });
        if (!isMounted) return;
        setSavedEntries(response.data?.data || []);
      } catch (error) {
        if (!isMounted) return;
        toast.error(error?.response?.data?.message || 'Failed to load saved dairy orders');
      } finally {
        if (isMounted) {
          setLoadingSavedEntries(false);
        }
      }
    };

    loadSavedEntries();

    return () => {
      isMounted = false;
    };
  }, [canViewPage, hasStoreContext, refreshKey, savedFromDate, savedToDate, deferredSavedSearch, savedStatusFilter, selectedStoreContext.storeInfoId, selectedStoreContext.vendorId]);

  useEffect(() => {
    if (!canViewPage || !hasStoreContext) return;

    let isMounted = true;

    const loadDraft = async () => {
      try {
        const response = await dairyOrdersAPI.getDraft({
          storeInfoId: selectedStoreContext.storeInfoId,
          vendorId: selectedStoreContext.vendorId,
        });
        if (!isMounted) return;
        const draft = response.data?.data;
        if (!draft) return;

        setTransactionType(draft.transactionType || 'inward');
        setTransactionDate(draft.transactionDate || todayKey());
        setReceivedBy(draft.receivedBy || user?.name || 'Current User');
        setAdjustedBy(draft.adjustedBy || '');
        setMemoNumber(draft.memoNumber || '');
        setRows(draft.lineItems?.length ? draft.lineItems.map(mapLineItemToRow) : [createEmptyRow()]);
        setEditingRecordId(draft.sourceRecordId || '');
        setEditingRecordSource(draft.sourceKind || '');
        setDraftMeta({
          draftId: draft.draftId || '',
          sourceRecordId: draft.sourceRecordId || '',
          sourceKind: draft.sourceKind || '',
        });
        setPreviewData(null);
        setIsConfirmModalOpen(false);
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Failed to load saved dairy review');
      }
    };

    loadDraft();

    return () => {
      isMounted = false;
    };
  }, [canViewPage, hasStoreContext, selectedStoreContext.storeInfoId, selectedStoreContext.vendorId, user?.name]);

  const productsByLookup = useMemo(() => {
    const map = new Map();
    const addUniqueLookup = (lookupValue, product) => {
      const key = normalizeText(lookupValue).toLowerCase();
      if (!key) return;
      if (map.has(key) && map.get(key) !== product) {
        map.set(key, null);
        return;
      }
      map.set(key, product);
    };

    products.forEach((product) => {
      addUniqueLookup(product.key, product);
      addUniqueLookup(product.productKey, product);
      addUniqueLookup(product.id, product);
      addUniqueLookup(product.label, product);
      addUniqueLookup(product.name, product);
      addUniqueLookup(product.sku, product);
    });
    return map;
  }, [products]);

  const totalQuantity = useMemo(
    () => rows.reduce((sum, row) => sum + toPositiveInteger(row.quantity), 0),
    [rows]
  );

  const hasDirtyState = useMemo(() => (
    memoNumber
    || adjustedBy
    || draftMeta.draftId
    || transactionType !== 'inward'
    || transactionDate !== todayKey()
    || normalizeText(receivedBy) !== normalizeText(user?.name || 'Current User')
    || rows.some((row) => row.productInput || row.quantity)
  ), [adjustedBy, draftMeta.draftId, memoNumber, receivedBy, rows, transactionDate, transactionType, user?.name]);
  const editingHasChanges = useMemo(() => {
    if (!editingBaselineEntry || !editingRecordId) return false;
    const normalizedReceivedBy = normalizeText(transactionType === 'inward' ? (user?.name || 'Current User') : receivedBy);
    const currentItems = sanitizeOrderRows(rows).map((item) => ({
      productKey: normalizeText(item.productKey),
      productId: normalizeText(item.productId),
      productName: normalizeText(item.productName || item.productInput),
      sku: normalizeText(item.sku),
      quantity: toPositiveInteger(item.quantity),
    }));
    const baselineItems = (editingBaselineEntry.lineItems || []).map((item) => ({
      productKey: normalizeText(item.productKey || item.product_key),
      productId: normalizeText(item.productId),
      productName: normalizeText(item.productName),
      sku: normalizeText(item.sku),
      quantity: toPositiveInteger(item.quantity),
    }));
    const sortItems = (items) => [...items].sort((a, b) => (
      `${a.productKey}::${a.productId}::${a.sku}::${a.productName}`.localeCompare(`${b.productKey}::${b.productId}::${b.sku}::${b.productName}`)
    ));
    const currentPayload = JSON.stringify({
      transactionType,
      transactionDate,
      receivedBy: normalizedReceivedBy,
      adjustedBy: normalizeText(adjustedBy),
      memoNumber: normalizeText(memoNumber),
      lineItems: sortItems(currentItems),
    });
    const baselinePayload = JSON.stringify({
      transactionType: editingBaselineEntry.transactionType || 'inward',
      transactionDate: editingBaselineEntry.transactionDate || todayKey(),
      receivedBy: normalizeText(editingBaselineEntry.receivedBy),
      adjustedBy: normalizeText(editingBaselineEntry.adjustedBy),
      memoNumber: normalizeText(editingBaselineEntry.memoNumber),
      lineItems: sortItems(baselineItems),
    });
    return currentPayload !== baselinePayload;
  }, [adjustedBy, editingBaselineEntry, editingRecordId, memoNumber, receivedBy, rows, transactionDate, transactionType, user?.name]);

  const syncRowProduct = (rowId, inputValue) => {
    setRows((current) => current.map((row) => {
      if (row.id !== rowId) return row;
      const match = productsByLookup.get(normalizeText(inputValue).toLowerCase());
      return {
        ...row,
        productInput: inputValue,
        productKey: match?.productKey || match?.key || '',
        productId: match?.id || '',
        productName: match?.name || '',
        sku: match?.sku || '',
      };
    }));
  };

  const updateRowQuantity = (rowId, value) => {
    const digitsOnly = String(value ?? '').replace(/[^0-9]/g, '');
    setRows((current) => current.map((row) => (
      row.id === rowId ? { ...row, quantity: digitsOnly } : row
    )));
  };

  const selectRowProduct = (rowId, product) => {
    const nextProductKey = getProductDuplicateKey(product?.productKey || product?.key || product?.id || product?.sku || product?.name);
    if (
      nextProductKey
      && rows.some((row) => (
        row.id !== rowId
        && getProductDuplicateKey(row.productKey || row.productId || row.sku || row.productName || row.productInput) === nextProductKey
      ))
    ) {
      setErrors((current) => ({
        ...current,
        [`row-${rowId}-product`]: 'This product is already added on this memo.',
      }));
      toast.error('This product is already added on this memo.');
      return;
    }

    setRows((current) => current.map((row) => (
      row.id === rowId
        ? {
            ...row,
            productInput: product?.name || '',
            productKey: product?.productKey || product?.key || '',
            productId: product?.id || '',
            productName: product?.name || '',
            sku: product?.sku || '',
          }
        : row
    )));
  };

  const addRow = () => {
    setRows((current) => [...current, createEmptyRow()]);
  };

  const removeRow = (rowId) => {
    setRows((current) => (
      current.length === 1
        ? [createEmptyRow()]
        : current.filter((row) => row.id !== rowId)
    ));
  };

  const resetForm = () => {
    setTransactionType('inward');
    setTransactionDate(todayKey());
    setReceivedBy(user?.name || 'Current User');
    setAdjustedBy('');
    setMemoNumber('');
    setRows([createEmptyRow()]);
    setErrors({});
    setPreviewData(null);
    setDraftMeta({ draftId: '', sourceRecordId: '', sourceKind: '' });
    setEditingRecordId('');
    setEditingRecordSource('');
    setEditingBaselineEntry(null);
    setIsConfirmModalOpen(false);
  };

  const sanitizeRows = () => sanitizeOrderRows(rows);

  const buildValidatedPayload = () => {
    const nextErrors = {};
    const lineItems = sanitizeRows();
    const normalizedMemo = normalizeText(memoNumber);
    const normalizedAdjustedBy = normalizeText(adjustedBy);
    const normalizedReceivedBy = normalizeText(transactionType === 'inward' ? (user?.name || 'Current User') : receivedBy);

    if (!transactionDate) nextErrors.transactionDate = 'Select a date.';
    if (!normalizedReceivedBy) nextErrors.receivedBy = 'Received By is required.';
    if (!normalizedAdjustedBy) nextErrors.adjustedBy = 'Adjusted By is required.';
    if (!normalizedMemo) {
      nextErrors.memoNumber = 'Memo Number is required.';
    } else if (!memoPattern.test(normalizedMemo)) {
      nextErrors.memoNumber = 'Use letters, numbers, hyphen, underscore, or slash only.';
    }

    if (!lineItems.length) {
      nextErrors.lineItems = 'Add at least one product row.';
    }

    const seenProducts = new Map();
    lineItems.forEach((row, index) => {
      const baseKey = `row-${row.id}`;
      if (!row.productId) nextErrors[`${baseKey}-product`] = `Select a valid product on row ${index + 1}.`;
      if (!row.quantity) nextErrors[`${baseKey}-quantity`] = `Enter quantity on row ${index + 1}.`;

      const productKey = getProductDuplicateKey(row.productKey || row.productId || row.sku);
      if (!productKey) return;
      const firstMatch = seenProducts.get(productKey);
      if (firstMatch) {
        nextErrors[`row-${firstMatch.id}-product`] = 'This product is already added on this memo.';
        nextErrors[`${baseKey}-product`] = `Duplicate of row ${firstMatch.index + 1}.`;
        nextErrors.lineItems = 'Remove duplicate products before saving this memo.';
        return;
      }
      seenProducts.set(productKey, { id: row.id, index });
    });

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      return null;
    }

    return {
      transactionType,
      transactionDate,
      receivedBy: normalizedReceivedBy,
      adjustedBy: normalizedAdjustedBy,
      memoNumber: normalizedMemo,
      sourceRecordId: editingRecordId || '',
      sourceKind: editingRecordSource || '',
      totalQuantity: lineItems.reduce((sum, row) => sum + row.quantity, 0),
      lineItems: lineItems.map((row) => ({
        productKey: row.productKey,
        productId: row.productId,
        productName: row.productName || row.productInput,
        sku: row.sku || null,
        quantity: row.quantity,
      })),
      storeInfoId: selectedStoreContext.storeInfoId,
      vendorId: selectedStoreContext.vendorId,
    };
  };

  const ensureStoreContext = () => {
    if (hasStoreContext) return true;
    toast.error('Select a store from the header before using Dairy Order Recording.');
    return false;
  };

  const saveCurrentFormForReview = () => {
    if (!ensureStoreContext()) return;
    setIsConfirmModalOpen(false);
    setPreviewData(null);
    const payload = buildValidatedPayload();
    if (!payload) {
      toast.error('Please fix the highlighted fields before continuing.');
      return;
    }

    const reviewSnapshot = {
      ...payload,
      ...draftMeta,
      lineItems: payload.lineItems,
      totalQuantity: payload.totalQuantity,
      reviewToken: createRowId(),
    };
    setPreviewData(reviewSnapshot);
    setIsConfirmModalOpen(true);

    const saveDraft = async () => {
      setIsSavingDraft(true);
      try {
        const response = await dairyOrdersAPI.saveDraft({
          ...payload,
          storeInfoId: selectedStoreContext.storeInfoId,
          vendorId: selectedStoreContext.vendorId,
          draftId: draftMeta.draftId || '',
          sourceRecordId: editingRecordId || draftMeta.sourceRecordId || '',
          sourceKind: editingRecordSource || draftMeta.sourceKind || '',
        });
        const draft = response.data?.data || {};
        const nextDraftMeta = {
          draftId: draft.draftId || draftMeta.draftId || '',
          sourceRecordId: draft.sourceRecordId || payload.sourceRecordId || draftMeta.sourceRecordId || '',
          sourceKind: draft.sourceKind || payload.sourceKind || draftMeta.sourceKind || DAIRY_RECORD_SOURCE,
        };
        setDraftMeta(nextDraftMeta);
        setPreviewData((current) => {
          if (!current || current.reviewToken !== reviewSnapshot.reviewToken) return current;
          return {
            ...current,
            ...nextDraftMeta,
          };
        });
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Failed to save dairy order review');
      } finally {
        setIsSavingDraft(false);
      }
    };

    saveDraft();
  };
  const handlePreview = (event) => {
    event.preventDefault();
    saveCurrentFormForReview();
  };

  const handleSaveFromReview = async () => {
    if (!previewData) return;
    if (!ensureStoreContext()) return;
    setIsSubmitting(true);
    try {
      const response = await dairyOrdersAPI.create({
        transactionType: previewData.transactionType,
        transactionDate: previewData.transactionDate,
        receivedBy: previewData.receivedBy,
        adjustedBy: previewData.adjustedBy,
        memoNumber: previewData.memoNumber,
        lineItems: previewData.lineItems || [],
        storeInfoId: selectedStoreContext.storeInfoId,
        vendorId: selectedStoreContext.vendorId,
        draftId: previewData.draftId || draftMeta.draftId || '',
        sourceRecordId: editingRecordId || previewData.sourceRecordId || draftMeta.sourceRecordId || '',
        sourceKind: editingRecordSource || previewData.sourceKind || draftMeta.sourceKind || '',
      });
      const createdRecord = response.data?.data;
      if (createdRecord?.id) {
        setLastSavedEntryId(createdRecord.id);
        setSavedEntries((current) => {
          const withoutDuplicate = current.filter((entry) => entry.id !== createdRecord.id);
          return [createdRecord, ...withoutDuplicate].slice(0, 8);
        });
      }
      setRefreshKey((current) => current + 1);
      setIsConfirmModalOpen(false);
      toast.success('Dairy order saved successfully');
      resetForm();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save dairy order');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClear = () => {
    const clearEntry = async () => {
      const draftId = draftMeta.draftId || previewData?.draftId;
      if (draftId) {
        try {
          await dairyOrdersAPI.deleteDraft(draftId);
        } catch (error) {
          toast.error(error?.response?.data?.message || 'Failed to clear dairy review draft');
          return;
        }
      }
      resetForm();
    };

    if (!hasDirtyState || window.confirm('Clear all entered dairy order details?')) {
      clearEntry();
    }
  };

  const handleCancel = () => {
    const cancelEntry = async () => {
      const draftId = draftMeta.draftId || previewData?.draftId;
      if (draftId) {
        try {
          await dairyOrdersAPI.deleteDraft(draftId);
        } catch (error) {
          toast.error(error?.response?.data?.message || 'Failed to cancel dairy review draft');
          return;
        }
      }
      navigate('/dashboard');
    };

    if (!hasDirtyState || window.confirm('Cancel this dairy order entry? Unsaved changes will be lost.')) {
      cancelEntry();
    }
  };

  const loadEntryForEdit = (entry) => {
    setTransactionType(entry.transactionType || 'inward');
    setTransactionDate(entry.transactionDate || todayKey());
    setReceivedBy(entry.receivedBy || user?.name || 'Current User');
    setAdjustedBy(entry.adjustedBy || '');
    setMemoNumber(entry.memoNumber || '');
    setRows(entry.lineItems?.length ? entry.lineItems.map(mapLineItemToRow) : [createEmptyRow()]);
    setEditingRecordId(entry.id || '');
    setEditingRecordSource(entry.source || DAIRY_RECORD_SOURCE);
    setEditingBaselineEntry(entry || null);
    setPreviewData(null);
    setDraftMeta({ draftId: '', sourceRecordId: '', sourceKind: '' });
    setIsConfirmModalOpen(false);
    setErrors({});
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleConfirmSavedEntry = async (entry) => {
    if (!entry?.id) return;
    if (!ensureStoreContext()) return;
    setIsSubmitting(true);
    try {
      const response = await dairyOrdersAPI.create({
        storeInfoId: selectedStoreContext.storeInfoId,
        vendorId: selectedStoreContext.vendorId,
        transactionType: normalizeText(entry.transactionType || transactionType).toLowerCase(),
        transactionDate: entry.transactionDate,
        receivedBy: entry.receivedBy,
        adjustedBy: entry.adjustedBy,
        memoNumber: entry.memoNumber,
        lineItems: (entry.lineItems || []).map((item) => ({
          productKey: item.productKey || item.product_key,
          productId: item.productId,
          productName: item.productName,
          sku: item.sku || null,
          quantity: toPositiveInteger(item.quantity),
        })),
        sourceRecordId: entry.id,
        sourceKind: entry.source || DAIRY_RECORD_SOURCE,
        confirmOnly: true,
      });
      const confirmedRecord = response.data?.data;
      if (confirmedRecord?.id) {
        setLastSavedEntryId(confirmedRecord.id);
        setConfirmedEntryMeta({
          id: confirmedRecord.id,
          memoNumber: confirmedRecord.memoNumber || entry.memoNumber,
          confirmedAt: confirmedRecord.updatedAt || confirmedRecord.createdAt || new Date().toISOString(),
        });
      }
      setRefreshKey((current) => current + 1);
      toast.success('Entry confirmed successfully');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to confirm saved entry');
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleRequestUnlock = async (entry) => {
    if (!entry?.id) return;
    if (!ensureStoreContext()) return;
    setIsSubmitting(true);
    try {
      await dairyOrdersAPI.create({
        storeInfoId: selectedStoreContext.storeInfoId,
        vendorId: selectedStoreContext.vendorId,
        sourceRecordId: entry.id,
        sourceKind: entry.source || DAIRY_RECORD_SOURCE,
        requestUnlock: true,
      });
      setRefreshKey((current) => current + 1);
      toast.success('Edit unlock request sent to admin');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to request edit unlock');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!canViewPage) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Daily Dairy Order Recording</h1>
        <div className="card">
          <p className="text-gray-500">Access denied.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="border-b border-slate-200 pb-2">
        <h1 className="text-[21px] font-bold tracking-tight text-slate-900">Daily Dairy Order Recording</h1>
        <p className="mt-0.5 text-[13px] leading-5 text-slate-600">
          Record inward and outward movement, review it, and re-edit confirmed entries whenever needed.
        </p>
        {hasStoreContext ? (
          <p className="mt-1 text-xs font-medium text-slate-500">
            Store: {selectedStoreContext.storeName || `Vendor ${selectedStoreContext.vendorId}`}
          </p>
        ) : null}
      </div>

      {!hasStoreContext ? (
        <div className="rounded-[14px] border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800">
          Select a store from the header to load dairy products and saved entries.
        </div>
      ) : null}

      <form onSubmit={handlePreview} className="space-y-4">
        <div className="grid gap-4 lg:grid-cols-[1.35fr_0.75fr]">
          <div className="rounded-[14px] border border-slate-200 bg-white p-4 shadow-[0_14px_40px_rgba(15,23,42,0.05)]">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Transaction Details</h2>
              </div>
              <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                {totalQuantity} total qty
              </div>
            </div>

              <div className="grid gap-4 lg:grid-cols-2">
                <label className="block">
                  <span className="label">Transaction Type</span>
                  <FilterSelect
                    className="w-full"
                    value={transactionType}
                    options={TRANSACTION_TYPE_OPTIONS}
                    onChange={(nextValue) => setTransactionType(String(nextValue))}
                  />
                </label>

              <label className="block">
                <span className="label">Date</span>
                <input
                  className="input rounded-lg"
                  type="date"
                  value={transactionDate}
                  onChange={(event) => setTransactionDate(event.target.value)}
                />
                {errors.transactionDate ? <span className="mt-1 block text-xs text-danger-600">{errors.transactionDate}</span> : null}
              </label>

              <label className="block">
                <span className="label">Received By</span>
                <input
                  className="input rounded-lg"
                  type="text"
                  value={transactionType === 'inward' ? (user?.name || 'Current User') : receivedBy}
                  onChange={(event) => setReceivedBy(event.target.value)}
                  readOnly={transactionType === 'inward'}
                />
                <span className="mt-1 block text-xs text-slate-500">
                  {transactionType === 'inward' ? 'Auto-filled from the current user.' : 'Editable for outward transactions.'}
                </span>
                {errors.receivedBy ? <span className="mt-1 block text-xs text-danger-600">{errors.receivedBy}</span> : null}
              </label>

              <label className="block">
                <span className="label">Adjusted By</span>
                <input
                  className="input rounded-lg"
                  type="text"
                  placeholder="Enter name"
                  value={adjustedBy}
                  onChange={(event) => setAdjustedBy(event.target.value)}
                />
                {errors.adjustedBy ? <span className="mt-1 block text-xs text-danger-600">{errors.adjustedBy}</span> : null}
              </label>

              <label className="block lg:col-span-2">
                <span className="label">Memo Number</span>
                <input
                  className="input rounded-lg"
                  type="text"
                  placeholder="Example: MEMO-1024"
                  value={memoNumber}
                  onChange={(event) => setMemoNumber(event.target.value)}
                />
                <span className="mt-1 block text-xs text-slate-500">Letters, numbers, hyphen, underscore, and slash are allowed.</span>
                {errors.memoNumber ? <span className="mt-1 block text-xs text-danger-600">{errors.memoNumber}</span> : null}
              </label>
            </div>
          </div>

          <div className="rounded-[14px] border border-slate-200 bg-white p-4 shadow-[0_14px_40px_rgba(15,23,42,0.05)]">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Quick Summary</h2>
                <p className="mt-1 text-sm text-slate-500">Live totals update while you type.</p>
              </div>
              {/* <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                Database-backed
              </span> */}
            </div>
            <div className="mt-4 grid gap-3">
              <div className="rounded-[14px] bg-slate-50 px-4 py-3.5">
                <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Transaction</p>
                <p className="mt-1.5 text-xl font-bold capitalize text-slate-900">{transactionType}</p>
              </div>
              <div className="rounded-[14px] bg-emerald-50 px-4 py-3.5">
                <p className="text-xs uppercase tracking-[0.18em] text-emerald-600">Product Rows</p>
                <p className="mt-1.5 text-xl font-bold text-emerald-900">{rows.filter((row) => row.productInput || row.quantity).length}</p>
              </div>
              <div className="rounded-[14px] bg-amber-50 px-4 py-3.5">
                <p className="text-xs uppercase tracking-[0.18em] text-amber-600">Total Quantity</p>
                <p className="mt-1.5 text-xl font-bold text-amber-900">{totalQuantity}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-[14px] border border-slate-200 bg-white p-4 shadow-[0_14px_40px_rgba(15,23,42,0.05)]">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Product Entries</h2>
            </div>
            <button
              type="button"
              onClick={addRow}
              disabled={!hasStoreContext}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
            >
              <Plus className="h-4 w-4" />
              Add Product
            </button>
          </div>

          <div className="hidden grid-cols-[minmax(260px,1.2fr)_110px_52px] gap-3 px-1 pb-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400 lg:grid">
            <span>Product</span>
            <span>Quantity</span>
            <span className="text-center">Remove</span>
          </div>

          <div className="overflow-visible rounded-[12px] border border-slate-200 bg-slate-50/60">
            {rows.map((row, index) => (
              <div
                key={row.id}
                style={{ zIndex: rows.length - index }}
                className={`relative grid gap-2 p-2.5 lg:grid-cols-[minmax(260px,1.2fr)_110px_52px] lg:items-center ${index !== rows.length - 1 ? 'border-b border-slate-200' : ''}`}
              >
                <label className="block">
                  <span className="label lg:hidden">Product #{index + 1}</span>
                  <SearchableProductInput
                    value={row.productInput}
                    products={products}
                    loading={loadingProducts}
                    placeholder={loadingProducts ? 'Loading products...' : 'Search or select product'}
                    onChangeText={(value) => syncRowProduct(row.id, value)}
                    onSelectProduct={(product) => selectRowProduct(row.id, product)}
                  />
                  {row.sku ? (
                    <div className="mt-0.5 text-[10px] text-slate-500">
                      <span className="truncate">{`SKU: ${row.sku}`}</span>
                    </div>
                  ) : null}
                  {errors[`row-${row.id}-product`] ? <span className="mt-1 block text-xs text-danger-600">{errors[`row-${row.id}-product`]}</span> : null}
                </label>

                <label className="block">
                  <span className="label lg:hidden">Quantity</span>
                  <input
                    className="input rounded-lg"
                    inputMode="numeric"
                    placeholder="0"
                    value={row.quantity}
                    onChange={(event) => updateRowQuantity(row.id, event.target.value)}
                  />
                  {errors[`row-${row.id}-quantity`] ? <span className="mt-1 block text-xs text-danger-600">{errors[`row-${row.id}-quantity`]}</span> : null}
                </label>

                <div className="flex items-end lg:items-start lg:justify-center">
                  <button
                    type="button"
                    onClick={() => removeRow(row.id)}
                    className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition hover:border-danger-200 hover:bg-danger-50 hover:text-danger-600"
                    aria-label={`Remove product row ${index + 1}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {errors.lineItems ? <p className="mt-4 text-sm text-danger-600">{errors.lineItems}</p> : null}

        </div>

        <div className="flex flex-wrap items-center justify-end gap-2.5">
          <button
            type="button"
            onClick={handleClear}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
          >
            <RotateCcw className="h-4 w-4" />
            Clear
          </button>
          <button
            type="button"
            onClick={handleCancel}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
          >
            <XCircle className="h-4 w-4" />
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSavingDraft || !hasStoreContext}
            className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-slate-900/15 transition hover:bg-slate-800"
          >
            <Eye className="h-4 w-4" />
            {isSavingDraft ? 'Saving review...' : 'Save & Review'}
          </button>
        </div>
      </form>

      <Modal isOpen={isConfirmModalOpen && !!previewData} onClose={() => setIsConfirmModalOpen(false)} size="xl">
        {previewData ? (
          <div className="rounded-[12px] border border-slate-200 bg-white p-4 shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">Confirmation</p>
                <h2 className="mt-1.5 text-2xl font-bold text-slate-900">Review and Save dairy order</h2>
                <p className="mt-1.5 text-sm text-slate-500">Please review once more, then click confirm to finalize and save.</p>
              </div>
              <div className="rounded-[12px] bg-slate-50 px-4 py-3 text-right">
                <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Total Quantity</p>
                <p className="mt-1.5 text-[30px] font-black text-slate-900">{previewData.totalQuantity}</p>
              </div>
            </div>

            <div className="mt-4 grid gap-3 lg:grid-cols-5">
              <div className="rounded-lg bg-slate-50 p-3.5">
                <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Type</p>
                <p className="mt-1.5 text-sm font-semibold capitalize text-slate-900">{previewData.transactionType}</p>
              </div>
              <div className="rounded-lg bg-slate-50 p-3.5">
                <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Date</p>
                <p className="mt-1.5 text-sm font-semibold text-slate-900">{previewData.transactionDate}</p>
              </div>
              <div className="rounded-lg bg-slate-50 p-3.5">
                <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Received By</p>
                <p className="mt-1.5 text-sm font-semibold text-slate-900">{previewData.receivedBy}</p>
              </div>
              <div className="rounded-lg bg-slate-50 p-3.5">
                <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Adjusted By</p>
                <p className="mt-1.5 text-sm font-semibold text-slate-900">{previewData.adjustedBy}</p>
              </div>
              <div className="rounded-lg bg-slate-50 p-3.5">
                <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Memo Number</p>
                <p className="mt-1.5 text-sm font-semibold text-slate-900">{previewData.memoNumber}</p>
              </div>
            </div>

            <div className="mt-4 table-scroll-shell-compact rounded-[12px] border border-slate-200">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Product</th>
                    <th className="px-4 py-3 font-semibold">SKU</th>
                    <th className="px-4 py-3 font-semibold text-right">Quantity</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {(previewData.lineItems?.length
                    ? previewData.lineItems
                    : sanitizeOrderRows(rows).map((row) => ({
                      productId: row.productId,
                      productName: row.productName || row.productInput,
                      sku: row.sku || null,
                      quantity: row.quantity,
                    }))).map((item, index) => (
                    <tr key={`${item.productId || item.productName || 'item'}-${index}`}>
                      <td className="px-4 py-3 font-medium text-slate-900">{item.productName}</td>
                      <td className="px-4 py-3 text-slate-500">{item.sku || '-'}</td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-800">{item.quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex flex-wrap justify-end gap-2.5">
              <button
                type="button"
                onClick={() => setIsConfirmModalOpen(false)}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
              >
                Edit Entry
              </button>
              <button
                type="button"
                onClick={handleSaveFromReview}
                disabled={isSubmitting || isSavingDraft}
                className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-600/20 transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Save className="h-4 w-4" />
                {isSubmitting ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        ) : null}
      </Modal>

      {confirmedEntryMeta ? (
        <div className="rounded-[14px] border border-emerald-200 bg-emerald-50 px-4 py-3 shadow-[0_10px_24px_rgba(16,185,129,0.12)]">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="inline-flex items-center gap-2 text-emerald-800">
              <CheckCircle2 className="h-4 w-4" />
              <span className="text-sm font-semibold">Confirmed</span>
              <span className="text-sm">Memo {confirmedEntryMeta.memoNumber || '-'}</span>
            </div>
            <span className="text-xs text-emerald-700">
              {formatDateTime(confirmedEntryMeta.confirmedAt)}
            </span>
          </div>
        </div>
      ) : null}

      <div className="rounded-[14px] border border-slate-200 bg-white p-4 shadow-[0_14px_40px_rgba(15,23,42,0.05)]">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">Confirmation Section</p>
            <h2 className="mt-1 text-xl font-bold text-slate-900">Saved entries ready for edit and confirm</h2>
            <p className="mt-1 text-xs text-slate-500">Showing the last 7 days by default. Adjust filters to narrow results.</p>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <label className="block min-w-[140px]">
              <span className="label">From</span>
              <input type="date" className="input rounded-lg" value={savedFromDate} max={savedToDate} onChange={(e) => setSavedFromDate(e.target.value || defaultSavedEntriesFromDate())} />
            </label>
            <label className="block min-w-[140px]">
              <span className="label">To</span>
              <input type="date" className="input rounded-lg" value={savedToDate} min={savedFromDate} max={todayKey()} onChange={(e) => setSavedToDate(e.target.value || todayKey())} />
            </label>
            <FilterSelect label="Status" value={savedStatusFilter} options={SAVED_ENTRY_STATUS_OPTIONS} onChange={setSavedStatusFilter} className="w-[160px]" />
            <label className="relative block min-w-[220px] flex-1">
              <span className="label">Search</span>
              <Search className="pointer-events-none absolute left-3 top-[2.55rem] h-4 w-4 text-gray-400" />
              <input className="input rounded-lg pl-10" placeholder="Memo, product, type..." value={savedSearch} onChange={(e) => setSavedSearch(e.target.value)} />
            </label>
          </div>
        </div>
        <div className="mt-3 w-fit rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-600">
          {savedEntries.length} entr{savedEntries.length === 1 ? 'y' : 'ies'} in range
        </div>
        <div className="mt-4 table-scroll-shell rounded-[14px] border border-slate-200">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3 font-semibold">Date</th>
                <th className="px-4 py-3 font-semibold">Memo Number</th>
                <th className="px-4 py-3 font-semibold">Type</th>
                <th className="px-4 py-3 font-semibold">Products</th>
                <th className="px-4 py-3 font-semibold text-right">Total Qty</th>
                <th className="px-4 py-3 font-semibold">Saved At</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {loadingSavedEntries ? (
                <tr>
                  <td colSpan="8" className="px-4 py-10 text-center text-sm text-slate-500">
                    Loading saved dairy orders...
                  </td>
                </tr>
              ) : null}

              {!loadingSavedEntries && savedEntries.map((entry) => {
                const isLatestSaved = lastSavedEntryId && entry.id === lastSavedEntryId;
                const productSummary = (entry.lineItems || []).slice(0, 2).map((item) => item.productName).filter(Boolean).join(', ');
                const isEditingThisEntry = editingRecordId && entry.id === editingRecordId;
                const isConfirmedEntry = String(entry.confirmationStatus || '').toLowerCase() === 'confirmed';
                const isEditLocked = isConfirmedEntry
                  && (
                    Boolean(entry.editControl?.confirmedLocked)
                    || !entry.editControl?.unlockedAt
                  );
                const unlockRequested = Boolean(entry.editControl?.unlockRequested);
                const isReviewingThisEntry = Boolean(
                  previewData
                  && (
                    previewData.sourceRecordId === entry.id
                    || (isEditingThisEntry && editingHasChanges)
                  )
                );

                return (
                  <tr key={entry.id} className={isLatestSaved ? 'bg-emerald-50/60' : 'hover:bg-slate-50/70'}>
                    <td className="px-4 py-3 text-slate-700">{formatDate(entry.transactionDate)}</td>
                    <td className="px-4 py-3 font-semibold text-slate-900">{entry.memoNumber}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${entry.transactionType === 'inward' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                        {entry.transactionType}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      <div className="font-medium text-slate-900">{entry.lineItems?.length || 0} product rows</div>
                      <div className="mt-0.5 text-xs text-slate-500">
                        {productSummary || '-'}
                        {entry.lineItems?.length > 2 ? ', ...' : ''}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-800">{entry.totalQuantity}</td>
                    <td className="px-4 py-3 text-slate-500">
                      <div>{formatDateTime(entry.createdAt)}</div>
                      <div className="mt-0.5 text-xs text-slate-400">Received by {entry.receivedBy || '-'}</div>
                    </td>
                    <td className="px-4 py-3">
                      {isReviewingThisEntry ? (
                        <span className="inline-flex rounded-full border border-amber-200 bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-700">
                          In Review
                        </span>
                      ) : isConfirmedEntry ? (
                        <span className="inline-flex rounded-full border border-emerald-200 bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                          Confirmed
                        </span>
                      ) : (
                        <span className="inline-flex rounded-full border border-sky-200 bg-sky-100 px-2.5 py-1 text-xs font-semibold text-sky-700">
                          Saved
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex flex-wrap justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => loadEntryForEdit(entry)}
                          disabled={isEditLocked}
                          className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                          Edit Again
                        </button>
                        {isEditLocked && !unlockRequested ? (
                          <button
                            type="button"
                            onClick={() => handleRequestUnlock(entry)}
                            disabled={isSubmitting || isSavingDraft}
                            className="inline-flex items-center gap-1.5 rounded-xl bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-amber-500 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            Request Admin Edit
                          </button>
                        ) : null}
                        {isEditLocked && unlockRequested ? (
                          <button
                            type="button"
                            disabled
                            className="inline-flex items-center gap-1.5 rounded-xl bg-slate-400 px-3 py-1.5 text-xs font-semibold text-white"
                          >
                            Requested
                          </button>
                        ) : null}
                        
                        {isEditingThisEntry && editingHasChanges ? (
                          <button
                            type="button"
                            onClick={saveCurrentFormForReview}
                            disabled={isSavingDraft || isSubmitting}
                            className="inline-flex items-center gap-1.5 rounded-xl bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            <Save className="h-3.5 w-3.5" />
                            {isSavingDraft ? 'Saving...' : 'Save & Review'}
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleConfirmSavedEntry(entry)}
                            disabled={isSubmitting || isSavingDraft || isConfirmedEntry}
                            className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-60 ${isConfirmedEntry ? 'bg-slate-400' : 'bg-emerald-600 hover:bg-emerald-500'}`}
                          >
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            {isConfirmedEntry ? 'Confirmed' : 'Confirm'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}

              {!loadingSavedEntries && savedEntries.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-10 text-center text-sm text-slate-500">
                    No saved dairy orders found yet.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DailyDairyOrderRecording;
